/**
 * Ported from daisyUI 5 — packages/daisyui/src/components/status.css
 * Source: https://github.com/saadeghi/daisyui (MIT, © Pouya Saadeghi)
 * License: MIT — see LICENSE-THIRD-PARTY.md.
 *
 * @component ry-status
 * @category  display
 * @summary   Tiny inline-block status dot. A small filled circle used to signal
 *            online / offline / busy / "in progress" / health state next to a
 *            label or avatar. Pure display — no behavior, no events.
 *
 * Models daisyUI's `<span class="status">` 1:1. Variants map to the ry-ui
 * palette (`--ry-color-primary`, `--ry-color-success`, ...). Sizes are xs/sm/
 * md/lg/xl matching daisyUI's `status-xs` ... `status-xl`.
 *
 * @attribute variant  'primary'|'secondary'|'accent'|'info'|'success'|'warning'|'error'|'neutral'  default=neutral  Color of the dot. Maps to the corresponding `--ry-color-*` token (`error` → `--ry-color-danger`; `neutral` → muted text).
 * @attribute size     'xs'|'sm'|'md'|'lg'|'xl'  default=md   Pixel diameter of the dot. md ≈ 8px (daisyUI's `size-2`).
 *
 * @content   None. ry-status renders no children — it's a pure visual marker.
 *            Pair with adjacent text via inline flow (the host is
 *            `inline-block` with `vertical-align: middle`).
 *
 * @dom
 *   ry-status[variant?][size?]
 *     (empty — styling is on the host)
 *
 * @gotcha  ry-status is purely a colored dot. Unlike `<ry-badge>` it does NOT
 *          render a count, label, or icon. If you need text, place text
 *          adjacent to the status: `<ry-status variant="success"></ry-status> Online`.
 *
 * @gotcha  daisyUI's `error` keeps its name on the variant attribute for
 *          parity, but resolves to ry-ui's `--ry-color-danger` token (ry-ui
 *          uses "danger" not "error" in its palette).
 *
 * @example default
 * ```html
 * <ry-status></ry-status>
 * ```
 *
 * @example variants
 * ```html
 * <ry-status variant="primary"></ry-status>
 * <ry-status variant="secondary"></ry-status>
 * <ry-status variant="accent"></ry-status>
 * <ry-status variant="info"></ry-status>
 * <ry-status variant="success"></ry-status>
 * <ry-status variant="warning"></ry-status>
 * <ry-status variant="error"></ry-status>
 * <ry-status variant="neutral"></ry-status>
 * ```
 *
 * @example sizes
 * ```html
 * <ry-status size="xs"></ry-status>
 * <ry-status size="sm"></ry-status>
 * <ry-status size="md"></ry-status>
 * <ry-status size="lg"></ry-status>
 * <ry-status size="xl"></ry-status>
 * ```
 *
 * @example inline-with-text
 * ```html
 * <span><ry-status variant="success"></ry-status> Online</span>
 * <span><ry-status variant="warning"></ry-status> Idle</span>
 * <span><ry-status variant="error"></ry-status> Offline</span>
 * ```
 *
 * @tutorial defaults-to-neutral-md
 * ```js
 * // Wrong assumption: a bare <ry-status> would gain a variant or size attr.
 * // The defaults are applied entirely via CSS — no attributes are reflected.
 * const el = mount('<ry-status></ry-status>');
 * await expectEqual(el.hasAttribute('variant'), false, 'no variant attr by default');
 * await expectEqual(el.hasAttribute('size'), false, 'no size attr by default');
 * await expectEqual(el.tagName.toLowerCase(), 'ry-status');
 * ```
 *
 * @tutorial variant-attribute-is-the-only-hook
 * ```js
 * // Wrong assumption: setting variant adds a class like .ry-status--success.
 * // ry-status is CSS-only — the [variant] attribute IS the selector.
 * const el = mount('<ry-status variant="success"></ry-status>');
 * await expectAttr(el, 'variant', 'success');
 * await expectEqual(el.className, '', 'no class is added');
 * ```
 *
 * @tutorial size-attribute-controls-diameter
 * ```js
 * const el = mount('<ry-status size="xl"></ry-status>');
 * await expectAttr(el, 'size', 'xl');
 * ```
 *
 * @see ry-badge
 * @see ry-tag
 */

import { RyElement } from '../core/ry-element.js';

export class RyStatus extends RyElement {
  setup(): void {
    // CSS-only component. No transformation, no event wiring.
    // The component exists as a class so it participates in the registry,
    // shows up in <ry> transform, and can grow behavior later without
    // breaking consumers.
  }
}

customElements.define('ry-status', RyStatus);
